import {PrivateFavorites} from './PrivateCollections.jsx';
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Bell, Heart, Home, RefreshCw, Search, Trash2 } from "lucide-react";
import { endpoints } from "../api.js";
import { t } from "../i18n.js";
import { canHydrateFavorite, favoriteTargetUrl, favoriteTitle, hydrateFavoriteDetails, savedSearchUrl } from "../utils/savedCollections.js";
import SavedSearchMatches from './SavedSearchMatches.jsx';
import "../collections.css";

const recordId = (record) => String(record?._id || record?.id || "");
const text = (value, language) => typeof value === "string" ? value : value && typeof value === "object" ? [value[language], value.en, value.ar].find((candidate) => typeof candidate === "string" && candidate) || "" : "";
const invalidResponse = () => new Error("The server returned an invalid response. Please try again.");
const areaLabels = { muscat: "Muscat coastal", al_mouj: "Al Mouj / Seeb", qurum: "Qurum / Bawshar", salalah: "Salalah coast", duqm: "Duqm" };
const filterLabels = {
  q: "collectionsKeywords", purpose: "purpose", propertyType: "propertyType", sort: "collectionsSort", area: "mapArea",
  governorateId: "collectionsGovernorate", wilayatId: "collectionsWilayat", neighborhoodId: "collectionsNeighborhood",
  foreignerEligible: "collectionsForeignOwnership", ITCEligible: "collectionsItc", commercialOwnershipAllowed: "collectionsCommercialOwnership",
  bedrooms: "beds", minPriceOMR: "collectionsMinPrice", maxPriceOMR: "collectionsMaxPrice", minAreaSqm: "collectionsMinArea", maxAreaSqm: "collectionsMaxArea", verifiedOnly: "collectionsVerified",
};

function useCollection(resource, services) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pending, setPending] = useState([]);
  const [itemErrors, setItemErrors] = useState({});
  const [removed, setRemoved] = useState(false);
  const alive = useRef(false);
  const version = useRef(0);
  const deleting = useRef(new Set());
  const hydration = useRef(null);
  const load = useCallback(async () => {
    if (deleting.current.size) return;
    hydration.current?.abort();
    const controller = new AbortController();
    hydration.current = controller;
    const requestId = ++version.current;
    setLoading(true);
    setError("");
    setRemoved(false);
    try {
      const result = await services.workspaceList(resource);
      if (!Array.isArray(result)) throw invalidResponse();
      if (alive.current && requestId === version.current) {
        const visible = result.slice(0, 100);
        setRecords(visible.map((record) => ({ ...record, favoriteDetail: resource === "favorites" && canHydrateFavorite(record, services) ? { status: "loading" } : null })));
        if (resource === "favorites") {
          void hydrateFavoriteDetails(visible, services, { signal: controller.signal, onResult: (id, detail) => {
            if (alive.current && requestId === version.current) setRecords((current) => current.map((record) => recordId(record) === id ? { ...record, favoriteDetail: detail } : record));
          } });
        }
      }
    } catch (failure) {
      if (alive.current && requestId === version.current) setError(failure.message || "Request failed");
    } finally {
      if (alive.current && requestId === version.current) setLoading(false);
    }
  }, [resource, services]);
  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; version.current += 1; hydration.current?.abort(); };
  }, [load]);

  async function remove(id) {
    if (!id || loading || deleting.current.has(id)) return false;
    const currentVersion = version.current;
    deleting.current.add(id);
    setPending([...deleting.current]);
    setRemoved(false);
    setItemErrors((current) => ({ ...current, [id]: "" }));
    try {
      const result = await services.workspaceDelete(resource, id);
      if (result !== null && result?.ok !== true) throw invalidResponse();
      if (!alive.current || version.current !== currentVersion) return false;
      setRecords((current) => current.filter((record) => recordId(record) !== id));
      setRemoved(true);
      return true;
    } catch (failure) {
      if (alive.current && version.current === currentVersion) {
        setRemoved(false);
        setItemErrors((current) => ({ ...current, [id]: failure.message || "Request failed" }));
      }
      return false;
    } finally {
      deleting.current.delete(id);
      if (alive.current) setPending([...deleting.current]);
    }
  }
  return { records, loading, error, pending, itemErrors, removed, load, remove };
}

function MatchingDetails(props){const [open,setOpen]=useState(false);return <details onToggle={event=>setOpen(event.currentTarget.open)}><summary>{props.language==='ar'?'مطابقة العقارات وسجلها':'Property matching and history'}</summary>{open&&<SavedSearchMatches {...props}/>}</details>;}
function SavedDate({ value, language }) {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return <span>{t(language, "collectionsSavedOn")} <time dateTime={date.toISOString()}>{new Intl.DateTimeFormat(language === "ar" ? "ar-OM" : "en-OM", { dateStyle: "medium" }).format(date)}</time></span>;
}

function SearchFilters({ filters, language }) {
  const params = new URLSearchParams(savedSearchUrl(filters).split("?")[1]);
  return <ul className="collection-filter-list">{[...params].map(([key, value]) => {
    const display = key === "area" ? t(language, areaLabels[value] || value) : key === "verifiedOnly" ? t(language, value === "true" ? "collectionsYes" : "collectionsNo") : t(language, value);
    return <li key={key}><span>{t(language, filterLabels[key])}</span> <bdi>{display}{key.includes("PriceOMR") ? " OMR" : key.includes("AreaSqm") ? ` ${t(language, "sqm")}` : ""}</bdi></li>;
  })}</ul>;
}

function FavoriteThumbnail({ source, title, language }) {
  const [failed, setFailed] = useState("");
  const safeSource = typeof source === "string" && (/^https?:\/\//i.test(source) || /^\/(?!\/)/.test(source) || /^data:image\/(?:png|jpe?g|webp);base64,/i.test(source)) ? source : "";
  return <div className="collection-thumbnail">{safeSource && failed !== safeSource ? <img src={safeSource} alt={title} loading="lazy" onError={() => setFailed(safeSource)} /> : <div><Home size={28} aria-hidden="true" /><span>{t(language, "noPhoto")}</span></div>}</div>;
}

function SavedCollection({ kind, language = "en", services = endpoints }) {
  const searches = kind === "savedSearches";
  const state = useCollection(kind, services);
  const removeButtons = useRef(new Map());
  const emptyAction = useRef(null);
  async function removeRecord(id) {
    const button = removeButtons.current.get(id);
    const focused = document.activeElement === button;
    const index = state.records.findIndex(record => recordId(record) === id);
    const neighbor = recordId(state.records[index + 1] || state.records[index - 1]);
    if (await state.remove(id)) requestAnimationFrame(() => {
      if (!focused || (document.activeElement !== document.body && document.activeElement !== button)) return;
      const candidates = [removeButtons.current.get(neighbor), ...removeButtons.current.values(), emptyAction.current];
      candidates.find(node => node?.isConnected && !node.disabled)?.focus();
    });
  }
  const location = useLocation();
  const Icon = searches ? Search : Heart;
  const titleKey = searches ? "collectionsSearchesTitle" : "collectionsFavoritesTitle";
  return <section className="saved-collection" aria-label={t(language, titleKey)}>
    <header className="collection-heading"><div><span className="eyebrow">{t(language, "collectionsEyebrow")}</span><h2>{t(language, titleKey)}</h2><p>{t(language, searches ? "collectionsSearchesIntro" : "collectionsFavoritesIntro")}</p></div><button type="button" className="collection-refresh" onClick={state.load} disabled={state.loading || state.pending.length > 0}><RefreshCw size={16} aria-hidden="true" />{t(language, "collectionsRefresh")}</button></header>
    {state.loading && <p role="status" className="collection-notice">{t(language, "collectionsLoading")}</p>}
    {state.error && <div role="alert" className="collection-notice collection-error"><p>{t(language, state.error)}</p><button type="button" onClick={state.load}>{t(language, "retry")}</button></div>}
    {state.removed && <p role="status" className="collection-notice collection-success">{t(language, searches ? "collectionsSearchRemoved" : "collectionsFavoriteRemoved")}</p>}
    {!state.loading && !state.error && state.records.length === 0 && <div className="collection-empty"><Icon size={32} aria-hidden="true" /><h3>{t(language, searches ? "collectionsNoSearches" : "collectionsNoFavorites")}</h3><p>{t(language, searches ? "collectionsSearchHint" : "collectionsFavoriteHint")}</p><Link ref={emptyAction} className="primary" to="/search">{t(language, "messagesBrowse")}<ArrowUpRight size={16} aria-hidden="true" /></Link></div>}
    {state.records.length >= 100 && <p className="collection-notice">{t(language, "collectionsLimit")}</p>}
    <div className="collection-grid" aria-busy={state.loading}>{state.records.map((record) => {
      const id = recordId(record);
      const deleting = state.pending.includes(id);
      const typeKey = { listing: "collectionsProperty", project: "collectionsProject", service_provider: "collectionsProvider", investment: "collectionsInvestment" }[record.targetType] || "collectionsItem";
      const detail = record.favoriteDetail;
      const hydrated = detail?.data;
      const title = searches ? text(record.title, language) || t(language, "collectionsSearch") : favoriteTitle(record, language) || favoriteTitle(hydrated, language) || t(language, typeKey);
      const href = searches ? savedSearchUrl(record.filters) : favoriteTargetUrl(record);
      const openKey = searches ? "collectionsOpenSearch" : record.targetType === "service_provider" ? "collectionsBrowseProviders" : record.targetType === "investment" ? "collectionsBrowseInvestments" : "collectionsOpenItem";
      return <article className="collection-card" key={id}>{!searches && <FavoriteThumbnail source={hydrated?.media?.[0] || record.media?.[0] || record.thumbnailUrl} title={title} language={language} />}<div className="collection-card-top"><span className="collection-card-icon"><Icon size={21} aria-hidden="true" /></span><button type="button" ref={node => node ? removeButtons.current.set(id, node) : removeButtons.current.delete(id)} className="collection-remove" disabled={deleting || state.loading || !id} aria-busy={deleting} onClick={() => removeRecord(id)} aria-label={`${t(language, "collectionsRemove")} ${title}`}><Trash2 size={15} aria-hidden="true" /><span>{t(language, deleting ? "collectionsRemoving" : "collectionsRemove")}</span></button></div><h3>{title}</h3>
        {searches ? <SearchFilters filters={record.filters} language={language} /> : <p className="collection-reference"><span>{t(language, typeKey)}</span>{hydrated && <span>{[hydrated.neighborhood, hydrated.governorate].filter((value) => typeof value === "string" && value).join(" · ")}</span>}</p>}
        {detail?.status === "loading" && <p className="collection-detail-status" role="status">{t(language, "collectionsLoadingDetails")}</p>}
        {detail?.status === "error" && <p className="collection-detail-status">{t(language, "collectionsDetailsRetry")}</p>}
        <div className="collection-meta"><SavedDate value={record.created} language={language} />{searches && typeof record.alertEnabled === "boolean" && <span><Bell size={13} aria-hidden="true" />{record.alertEnabled?(language==="ar"?"تفضيل التنبيه محفوظ؛ الإرسال غير مهيأ":"Alert preference saved; delivery unavailable"):t(language,"collectionsAlertsOff")}</span>}</div>
        {searches&&typeof services.savedSearchMatches==='function'&&<MatchingDetails record={record} language={language} services={services}/>}
        {state.itemErrors[id] && <p role="alert" className="collection-error">{t(language, state.itemErrors[id])}</p>}
        {detail?.status === "unavailable" ? <p className="collection-unavailable">{t(language, "collectionsItemUnavailable")}</p> : href ? <Link className="text-link collection-open" to={href} state={{ from: location.pathname + location.search }}>{t(language, openKey)}<ArrowUpRight size={17} aria-hidden="true" /></Link> : <p className="collection-unavailable">{t(language, "collectionsNoLink")}</p>}
      </article>;
    })}</div>
  </section>;
}

export function SavedSearchesPanel(props) { return <SavedCollection {...props} kind="savedSearches" />; }
export function FavoritesPanel(props) { return <PrivateFavorites {...props} />; }
